// Internal value store backing the renderer. A tiny external store consumed via
// useSyncExternalStore: holds the whole value tree, applies immutable updates by
// path, and notifies subscribers. Living outside React lets the imperative
// handle (and non-React / agent hosts) mutate values without round-tripping
// through component state — handle methods call these mutators directly.

import type { DynamicFormValue } from './types'
import { setFieldValue as setIn } from './value'

export interface FormStore {
  getSnapshot(): DynamicFormValue
  subscribe(listener: () => void): () => void
  /** Replace the entire value tree. */
  setValue(next: DynamicFormValue): void
  /** Immutably set a single path (dot + bracket notation). */
  setFieldValue(path: string, value: unknown): void
  /** Restore the seed value the store was created with. */
  reset(): void
}

export function createFormStore(initial: DynamicFormValue): FormStore {
  const seed = initial
  let snapshot = initial
  const listeners = new Set<() => void>()

  function emit(): void {
    for (const listener of listeners) listener()
  }

  return {
    getSnapshot: () => snapshot,
    subscribe(listener) {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    setValue(next) {
      if (next === snapshot) return
      snapshot = next
      emit()
    },
    setFieldValue(path, value) {
      snapshot = setIn(snapshot, path, value)
      emit()
    },
    reset() {
      if (snapshot === seed) return
      snapshot = seed
      emit()
    },
  }
}
